/**
 * Expense Service
 * Manages expense tracking and approval workflow
 */

import { supabase } from '@/lib/supabase'
import type { Expense } from './financeService'

// =====================================================
// EXPENSE SERVICE
// =====================================================

export class ExpenseService {
  /**
   * Get expenses with filters
   */
  static async getExpenses(
    churchId: string,
    filters?: {
      status?: string
      vendorId?: string
      startDate?: string
      endDate?: string
      category?: string
      search?: string
      limit?: number
      offset?: number
    }
  ): Promise<{ data: Expense[]; count: number }> {
    let query = supabase
      .from('expenses')
      .select(`
        *,
        vendor:vendors(*),
        expense_account:chart_of_accounts!expense_account_id(*),
        fund:funds(*),
        journal_entry:general_ledger(*)
      `, { count: 'exact' })
      .eq('church_id', churchId)
      .order('expense_date', { ascending: false })

    if (filters?.status) {
      query = query.eq('status', filters.status)
    }

    if (filters?.vendorId) {
      query = query.eq('vendor_id', filters.vendorId)
    }

    if (filters?.startDate) {
      query = query.gte('expense_date', filters.startDate)
    }

    if (filters?.endDate) {
      query = query.lte('expense_date', filters.endDate)
    }

    if (filters?.category) {
      query = query.eq('expense_category', filters.category)
    }

    if (filters?.search) {
      query = query.or(`description.ilike.%${filters.search}%,vendor_name.ilike.%${filters.search}%,invoice_number.ilike.%${filters.search}%`)
    }

    if (filters?.limit) {
      query = query.limit(filters.limit)
    }

    if (filters?.offset) {
      query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1)
    }

    const { data, error, count } = await query

    if (error) throw error
    return { data: data || [], count: count || 0 }
  }

  /**
   * Get expense by ID
   */
  static async getExpenseById(expenseId: string): Promise<Expense | null> {
    const { data, error } = await supabase
      .from('expenses')
      .select(`
        *,
        vendor:vendors(*),
        expense_account:chart_of_accounts!expense_account_id(*),
        fund:funds(*),
        journal_entry:general_ledger(*),
        approval_history:expense_approval_history(*)
      `)
      .eq('id', expenseId)
      .single()

    if (error) throw error
    return data
  }

  /**
   * Create expense
   */
  static async createExpense(
    expense: Partial<Expense>,
    createdBy: string
  ): Promise<Expense> {
    // Generate expense number if not provided
    if (!expense.expense_number) {
      const date = new Date(expense.expense_date!)
      const prefix = 'EXP'
      const datePart = date.toISOString().slice(0, 7).replace('-', '')
      expense.expense_number = `${prefix}-${datePart}-${Math.random().toString(36).substring(2, 10).toUpperCase()}`
    }

    const { data, error } = await supabase
      .from('expenses')
      .insert([{
        ...expense,
        status: expense.status || 'draft',
        created_by: createdBy
      }])
      .select()
      .single()

    if (error) throw error
    return data
  }

  /**
   * Update expense (only if not posted to ledger)
   */
  static async updateExpense(
    expenseId: string,
    updates: Partial<Expense>
  ): Promise<Expense> {
    // Check if already posted
    const existing = await this.getExpenseById(expenseId)
    if (existing?.is_posted_to_ledger) {
      throw new Error('Cannot modify expenses that have been posted to the ledger')
    }

    const { data, error } = await supabase
      .from('expenses')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', expenseId)
      .select()
      .single()

    if (error) throw error
    return data
  }

  /**
   * Submit expense for approval
   */
  static async submitExpense(
    expenseId: string,
    submittedBy: string
  ): Promise<void> {
    const { error } = await supabase
      .from('expenses')
      .update({
        status: 'pending_approval',
        submitted_by: submittedBy,
        submitted_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', expenseId)

    if (error) throw error
  }

  /**
   * Approve expense
   */
  static async approveExpense(
    expenseId: string,
    approvedBy: string,
    comments?: string
  ): Promise<void> {
    // Update expense status
    const { error: updateError } = await supabase
      .from('expenses')
      .update({
        status: 'approved',
        approved_by: approvedBy,
        approved_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', expenseId)

    if (updateError) throw updateError

    // Add to approval history
    const { error: historyError } = await supabase
      .from('expense_approval_history')
      .insert([{
        expense_id: expenseId,
        approval_level: 1,
        approver_id: approvedBy,
        action: 'approved',
        comments
      }])

    if (historyError) throw historyError

    // Note: Triggers will automatically:
    // 1. Create vendor transaction (increase A/P)
    // 2. Post journal entry (DR Expense, CR A/P)
  }

  /**
   * Reject expense
   */
  static async rejectExpense(
    expenseId: string,
    rejectedBy: string,
    reason: string
  ): Promise<void> {
    // Update expense status
    const { error: updateError } = await supabase
      .from('expenses')
      .update({
        status: 'rejected',
        rejected_by: rejectedBy,
        rejected_at: new Date().toISOString(),
        rejection_reason: reason,
        updated_at: new Date().toISOString()
      })
      .eq('id', expenseId)

    if (updateError) throw updateError

    // Add to approval history
    const { error: historyError } = await supabase
      .from('expense_approval_history')
      .insert([{
        expense_id: expenseId,
        approval_level: 1,
        approver_id: rejectedBy,
        action: 'rejected',
        comments: reason
      }])

    if (historyError) throw historyError
  }

  /**
   * Mark expense as paid (supports partial payments)
   */
  static async markAsPaid(
    expenseId: string,
    paymentDetails: {
      payment_method: string
      payment_reference: string
      payment_date: string
      payment_amount?: string
    },
    paidBy: string
  ): Promise<void> {
    // Get current expense details
    const expense = await this.getExpenseById(expenseId)
    if (!expense) throw new Error('Expense not found')

    // Calculate payment amount (default to full balance due)
    const paymentAmount = paymentDetails.payment_amount
      ? parseFloat(paymentDetails.payment_amount)
      : expense.total_amount - (expense.amount_paid || 0)

    // Calculate new totals
    const newAmountPaid = (expense.amount_paid || 0) + paymentAmount
    const newBalanceDue = expense.total_amount - newAmountPaid

    // Determine new status
    let newStatus: 'paid' | 'partially_paid' = 'paid'
    if (newBalanceDue > 0.01) { // Small threshold for floating point errors
      newStatus = 'partially_paid'
    }

    // Update expense
    const { error } = await supabase
      .from('expenses')
      .update({
        status: newStatus,
        payment_method: paymentDetails.payment_method,
        payment_reference: paymentDetails.payment_reference,
        payment_date: paymentDetails.payment_date,
        amount_paid: newAmountPaid,
        balance_due: newBalanceDue,
        is_fully_paid: newStatus === 'paid',
        updated_by: paidBy,
        updated_at: new Date().toISOString()
      })
      .eq('id', expenseId)

    if (error) throw error

    // Note: Trigger will automatically:
    // 1. Create vendor payment transaction (decrease A/P by payment amount)
    // 2. Post journal entry (DR A/P, CR Cash for payment amount)
  }

  /**
   * Delete expense (only if draft)
   */
  static async deleteExpense(expenseId: string): Promise<void> {
    const existing = await this.getExpenseById(expenseId)
    if (existing?.status !== 'draft') {
      throw new Error('Only draft expenses can be deleted')
    }

    const { error } = await supabase
      .from('expenses')
      .delete()
      .eq('id', expenseId)

    if (error) throw error
  }

  /**
   * Get pending approvals for a user
   */
  static async getPendingApprovals(churchId: string, approverId: string): Promise<Expense[]> {
    const { data, error } = await supabase
      .from('expenses')
      .select(`
        *,
        vendor:vendors(*),
        expense_account:chart_of_accounts!expense_account_id(*)
      `)
      .eq('church_id', churchId)
      .eq('status', 'pending_approval')
      .eq('current_approver_id', approverId)
      .order('submitted_at', { ascending: true })

    if (error) throw error
    return data || []
  }

  /**
   * Get expense summary by category
   */
  static async getExpenseSummaryByCategory(
    churchId: string,
    startDate: string,
    endDate: string
  ): Promise<Record<string, { amount: number; count: number }>> {
    const { data, error } = await supabase
      .from('expenses')
      .select('expense_category, total_amount')
      .eq('church_id', churchId)
      .eq('status', 'paid')
      .gte('expense_date', startDate)
      .lte('expense_date', endDate)

    if (error) throw error

    const summary: Record<string, { amount: number; count: number }> = {}

    data?.forEach(expense => {
      const category = expense.expense_category || 'Uncategorized'
      if (!summary[category]) {
        summary[category] = { amount: 0, count: 0 }
      }
      summary[category].amount += expense.total_amount
      summary[category].count += 1
    })

    return summary
  }
}

export default ExpenseService
